import { FAIcon, IconStyle } from '@nextgen-web-framework/all';
import { type IconName } from '@fortawesome/pro-regular-svg-icons';
import { ImageComponent, VideoContainer, SourceTypeEnums } from '../../Molecules';
import type { IGalleryState, RenderNavigationBtnType, ThumbnailTrayType, HeroGalleryType } from './types/propsTypes';
import { MediaType, SelectorIdEnum } from './types/enums';
import { getImageId, getImageStyleType } from './productGalleryV2Util';

export const renderNavigationBtn = ({
  direction,
  shouldShowChevrons,
  handleNavClick,
  handleKeyBoardNavigation,
  styles,
}: RenderNavigationBtnType) => {
  const identifier = `cpw-product-gallery-${direction}-control`;
  return (
    shouldShowChevrons && (
      <button
        onClick={() => handleNavClick(direction)}
        className={styles[identifier]}
        onKeyUp={(e) => handleKeyBoardNavigation(e, direction)}
        data-testid={identifier}
        key={`nav${direction}`}
      >
        <FAIcon iconName={`chevron-${direction}` as IconName} iconStyle={IconStyle.REGULAR} />
      </button>
    )
  );
};

export const thumbnailTray = ({
  mediaList,
  activeIndex,
  onThumbnailClick,
  handleKeyBoardNavigation,
  sourceKey,
  styles,
}: ThumbnailTrayType) => (
  <>
    {mediaList?.map((media: IGalleryState, index: number) => {
      const { main, id, type } = media;
      const { altText = '', publicUrl = '', mediaId = '' } = main || {};
      const isActive = index === activeIndex;
      const customId = `${id}-${sourceKey}`;
      return (
        <>
          <div
            className={`${styles['galleryContainerScrollThumbnail']} ${
              isActive ? styles['galleryContainerScrollActive'] : ''
            }`}
            onClick={() => onThumbnailClick(index)}
            onKeyUp={(e) => handleKeyBoardNavigation(e, undefined)}
            key={customId}
            data-testid={[`gallery-elem-${customId}-test-id`, isActive ? '-active' : ''].join('')}
            role="button"
            aria-label={altText}
            tabIndex={0}
            id={getImageId(id, sourceKey)}
          >
            <>
              {type?.toLowerCase() === MediaType.IMAGE && (
                <ImageComponent
                  link={publicUrl || ''}
                  alt={altText}
                  imagetype={getImageStyleType(sourceKey).thumbnail}
                  data-testid="image-thumbnail-test-id"
                  loading="eager"
                  priority={true}
                  key={`${id}image`}
                />
              )}
              {type?.toLowerCase() === MediaType.VIDEO && (
                <VideoContainer
                  videoId={mediaId || ''}
                  isThumbnail={true}
                  sourceKey={`${sourceKey}-${SourceTypeEnums.THUMBNAIL}`}
                  key={`${id}video`}
                />
              )}
            </>
          </div>
        </>
      );
    })}
  </>
);

export const heroGallery = ({
  styles,
  media,
  sourceKey,
  isGalleryEmpty = false,
  index,
  isModal,
  showDummy = false,
  className = '',
}: HeroGalleryType) => {
  const { main, id, type } = media || {};
  const { altText = '', publicUrl = '', mediaId = '' } = main || {};
  const isDummy = index === -1;
  const newId = `${id}${isDummy ? '-dummy' : ''}`;
  const dataId = getImageId(newId, sourceKey, SelectorIdEnum.HERO_GALLERY_SLIDE);
  const isIndexOne = index === 1;

  return (
    <div
      className={`${styles['hero-gallery-slide']} ${isDummy ? `${styles['hero-gallery-slide-dummy-node']} ${showDummy && styles['hero-gallery-slide-dummy-node-hover']}` : ''} ${className}`}
      id={dataId}
      data-testid={`${dataId}${isIndexOne ? '-active' : ''}`}
      key={newId}
    >
      {(type?.toLowerCase() === MediaType.IMAGE || isGalleryEmpty) && (
        <ImageComponent
          link={publicUrl || ''}
          alt={altText}
          imagetype={getImageStyleType(sourceKey).main}
          data-testid="image-main-test-id"
          key={`${dataId}-image`}
          // Lazy load offscreen images of gallery
          {...(isIndexOne ? { loading: 'eager', priority: true } : { loading: 'lazy' })}
        />
      )}
      {type?.toLowerCase() === MediaType.VIDEO && (
        <VideoContainer
          videoId={mediaId || ''}
          sourceKey={`${sourceKey}-${SourceTypeEnums.MAIN}`}
          className={
            sourceKey === SourceTypeEnums.PRODUCT_DETAIL_GALLERY ? styles['cpw-product-details-video-container'] : ''
          }
          playOnButtonClick={isModal}
          key={`${dataId}-video`}
        />
      )}
    </div>
  );
};
